// eBay's daily message limit in the agent app (2026-10-29,
// docs/decisions/2026-10-29-ebay-new-messages-attach-listing.md).
//
// - The banner over the reply box: while eBay refused a message for its daily
//   limit less than an hour ago on a contact's account, and no send on that
//   account has gone through since (the server's ledger decides; the list
//   rides on the 30 s session tick as `ebayDailyLimit`).
// - The refused send: the server's text starts with DAILY_LIMIT_TEXT_PREFIX
//   (a reply's lastSendError) or carries errorCode ebay_daily_limit (a step);
//   the panel shows the copy button and "Sent response through eBay Portal" for it.
// Pure, plus a tiny store the session tick feeds (App.jsx).

export const EBAY_DAILY_LIMIT_CODE = 'ebay_daily_limit'
export const DAILY_LIMIT_TEXT_PREFIX = "eBay's daily message limit for "
export const DAILY_LIMIT_STEP_STATE = "eBay's daily message limit: send this one in Seller Hub, then click 'Sent response through eBay Portal'"
export const MARKED_SENT_NOTE = 'Sent response through eBay Portal (eBay daily message limit)'
export const BANNER_WINDOW_MS = 60 * 60 * 1000

export const EBAY_MESSAGE_STEP_TYPES = Object.freeze([
  'send_ebay_member_message',
  'sales_hold_send_ebay_message',
  'automated_return_notice_send_message',
  'resend_return_label_send_message',
])

export function isDailyLimitText(text) {
  return typeof text === 'string' && text.startsWith(DAILY_LIMIT_TEXT_PREFIX)
}

export function isDailyLimitStepState(stepState) {
  return stepState?.status === 'failed' && (stepState?.errorCode === EBAY_DAILY_LIMIT_CODE || isDailyLimitText(stepState?.error))
}

// The brand of an account or company code: '1A_EBAY' / '1A' -> '1A'.
export function brandOf(code) {
  const c = String(code ?? '')
  if (/^AM(_|$)/.test(c)) return 'AM'
  if (/^1A(_|$)/.test(c)) return '1A'
  return null
}

// h:mm AM, Eastern.
export function etTime(iso) {
  const d = new Date(iso)
  if (Number.isNaN(d.getTime())) return ''
  return `${d.toLocaleTimeString('en-US', { timeZone: 'America/New_York', hour: 'numeric', minute: '2-digit' })} ET`
}

// The live entry for this contact, or null. An eBay conversation is on its
// own account; another contact (an email, a task) is on the eBay account of
// the brand its eBay message steps name.
export function bannerEntryFor(entries, { channel = null, company = null, actions = [], now = Date.now() } = {}) {
  const live = (entries ?? []).filter((e) => e && now - new Date(e.lastRefusalAt).getTime() < BANNER_WINDOW_MS)
  if (!live.length) return null
  if (channel === 'ebay') return live.find((e) => e.account === company) ?? null
  const ebaySteps = (actions ?? []).filter((a) => EBAY_MESSAGE_STEP_TYPES.includes(a?.type))
  if (!ebaySteps.length) return null
  const brands = new Set(ebaySteps.map((a) => brandOf(a?.params?.company) ?? brandOf(company)).filter(Boolean))
  return live.find((e) => brands.has(e.brand ?? brandOf(e.account))) ?? null
}

export function bannerText(entry) {
  return `eBay's daily message limit was reached at ${etTime(entry.hitAt)}. Messages may not send right now; if Send fails, use Seller Hub.`
}

// A step's state restored from the suggestion's call log after a reload: the
// newest daily-limit stop note or Sent response through eBay Portal line for each step.
// { [index]: stepState }.
export function dailyLimitStepStates(suggestion) {
  const calls = suggestion?.modelMeta?.apiCalls ?? []
  const states = {}
  for (const c of calls) {
    if (!Number.isInteger(c?.stepIndex) || typeof c.label !== 'string') continue
    if (c.label.startsWith(DAILY_LIMIT_STEP_STATE)) {
      const account = c.label.match(/account ([A-Z0-9_]+)$/)?.[1] ?? null
      states[c.stepIndex] = { status: 'failed', result: null, errorCode: EBAY_DAILY_LIMIT_CODE, dailyLimit: { account }, error: dailyLimitText(account) }
    } else if (c.label === MARKED_SENT_NOTE) {
      states[c.stepIndex] = { status: 'success', error: null, result: { sentInSellerHub: true } }
    } else if (!c.error && /^(sendMessage|AddMemberMessageAAQToPartner)/.test(c.label) && states[c.stepIndex]?.errorCode === EBAY_DAILY_LIMIT_CODE) {
      // A later Process of the same step went through: no longer stopped.
      delete states[c.stepIndex]
    }
  }
  return states
}

export function accountLabel(account) {
  if (account === '1A_EBAY') return '1A eBay'
  if (account === 'AM_EBAY') return 'AM eBay'
  return account || 'eBay'
}

export function dailyLimitText(account) {
  return `${DAILY_LIMIT_TEXT_PREFIX}${accountLabel(account)} was reached, so eBay didn't send this. Copy the message and send it in Seller Hub, then click 'Sent response through eBay Portal'.`
}

// The facts a person needs in Seller Hub, from the step's own params: the
// buyer's username, the order and the item. Missing ones are left out.
export function sellerHubFacts(action) {
  const p = action?.params ?? {}
  const facts = []
  if (p.buyerId) facts.push(['Buyer', String(p.buyerId)])
  const order = p.erpOrderId ?? p.orderNumber ?? null
  if (order) facts.push(['Order', String(order)])
  if (p.itemId) facts.push(['Item', String(p.itemId)])
  return facts
}

// The store the session tick feeds.
let current = []
const listeners = new Set()
export function setEbayDailyLimits(list) {
  current = Array.isArray(list) ? list : []
  for (const fn of listeners) fn(current)
}
export function getEbayDailyLimits() { return current }
export function subscribeEbayDailyLimits(fn) {
  listeners.add(fn)
  return () => listeners.delete(fn)
}
